import { useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import type { GraphIssue } from "@velizhanin-dev/bot-scenario"
import axios from "axios"
import { Loader2, Rocket } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	getPublishPreview,
	publishScenario,
	type ScenarioDraft,
} from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Textarea } from "@/shared/ui/textarea"
import { countDiff, DiffView } from "./diff-view"
import { Disclosure } from "@/shared/ui/disclosure"

function IssueList({
	issues,
	onPickNode,
}: {
	issues: GraphIssue[]
	onPickNode: (nodeId: string) => void
}) {
	return (
		<ul className="space-y-1">
			{issues.map((issue, index) => (
				<li key={`${issue.code}-${index}`}>
					<Button
						type="button"
						variant="ghost"
						disabled={!issue.nodeId}
						onClick={() => issue.nodeId && onPickNode(issue.nodeId)}
						className={cn(
							"h-auto justify-start p-0 text-left text-sm hover:bg-transparent disabled:opacity-100 enabled:hover:underline",
							issue.severity === "error" ? "text-red-400" : "text-yellow-400",
						)}
					>
						{issue.message}
					</Button>
				</li>
			))}
		</ul>
	)
}

interface PublishDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	draft: ScenarioDraft
	/** Правки ещё уходят на сервер: предпросмотр был бы неполным. */
	isSaving: boolean
	onPickNode: (nodeId: string) => void
}

/**
 * Публикация ровно той ревизии, которую админ видел в предпросмотре: если черновик
 * успели изменить, api ответит 409 и предпросмотр перезагрузится.
 */
export function PublishDialog({
	open,
	onOpenChange,
	draft,
	isSaving,
	onPickNode,
}: PublishDialogProps) {
	const queryClient = useQueryClient()
	const [comment, setComment] = useState("")
	const [publishing, setPublishing] = useState(false)

	const preview = useQuery({
		queryKey: [...botBuilderKeys.scenario(draft.id), "publish-preview", draft.revision],
		queryFn: () => getPublishPreview(draft.id),
		enabled: open && !isSaving,
		staleTime: 0,
		gcTime: 0,
	})

	useEffect(() => {
		if (!open) setComment("")
	}, [open])

	const data = preview.data
	const errors = data?.issues.filter((issue) => issue.severity === "error") ?? []
	const warnings = data?.issues.filter((issue) => issue.severity === "warning") ?? []
	const changes = data ? countDiff(data.diff) : 0

	const publish = async () => {
		if (!data) return
		setPublishing(true)
		try {
			const result = await publishScenario(draft.id, {
				baseRevision: data.revision,
				comment: comment.trim() || undefined,
			})
			queryClient.setQueryData<ScenarioDraft>(botBuilderKeys.scenario(draft.id), (current) =>
				current
					? {
							...current,
							latestVersion: {
								id: result.version.id,
								number: result.version.number,
								publishedAt: result.version.publishedAt,
							},
						}
					: current,
			)
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.versions(draft.id) })
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios })
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.bots })
			toast.success(
				draft.bots.length > 0
					? `Версия ${result.version.number} опубликована: боты уже отвечают по ней`
					: `Версия ${result.version.number} опубликована`,
			)
			onOpenChange(false)
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 409) {
				toast.warning("Черновик изменился. Проверьте изменения ещё раз")
				await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenario(draft.id) })
			} else {
				toast.error(extractApiErrorMessage(error, "Не удалось опубликовать"))
			}
		} finally {
			setPublishing(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{data?.latestVersionNumber
							? `Публикация версии ${data.latestVersionNumber + 1}`
							: "Первая публикация"}
					</DialogTitle>
					<DialogDescription>
						{draft.bots.length > 0
							? `Сразу после публикации по ней начнут отвечать: ${draft.bots.map((bot) => `@${bot.username}`).join(", ")}.`
							: "К сценарию пока не подключён ни один бот."}
					</DialogDescription>
				</DialogHeader>

				{isSaving || preview.isLoading ? (
					<p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" />
						{isSaving ? "Дожидаемся сохранения правок…" : "Проверяем черновик…"}
					</p>
				) : preview.isError || !data ? (
					<p className="py-6 text-sm text-red-400">
						{extractApiErrorMessage(preview.error, "Не удалось проверить черновик")}
					</p>
				) : (
					<div className="space-y-4">
						{errors.length > 0 ? (
							<div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
								<p className="mb-2 text-sm font-medium">
									Исправьте ошибки, чтобы опубликовать ({errors.length})
								</p>
								<IssueList
									issues={errors}
									onPickNode={(nodeId) => {
										onOpenChange(false)
										onPickNode(nodeId)
									}}
								/>
							</div>
						) : null}

						{!data.hasChanges ? (
							<p className="text-sm text-muted-foreground">
								С версии {data.latestVersionNumber} ничего не изменилось.
							</p>
						) : data.latestVersionNumber ? (
							<div>
								<p className="mb-2 text-sm font-medium">Что изменилось · {changes}</p>
								<DiffView diff={data.diff} draft={draft} />
							</div>
						) : (
							<p className="text-sm text-muted-foreground">
								Блоков в сценарии: {draft.nodes.length}, воронок: {draft.flows.length}.
							</p>
						)}

						{warnings.length > 0 ? (
							<Disclosure
								titleClassName="text-yellow-400 hover:text-yellow-300"
								title={`Предупреждения · ${warnings.length}: публикации не мешают`}
							>
								<div>
									<IssueList
										issues={warnings}
										onPickNode={(nodeId) => {
											onOpenChange(false)
											onPickNode(nodeId)
										}}
									/>
								</div>
							</Disclosure>
						) : null}

						{data.canPublish ? (
							<Textarea
								aria-label="Комментарий к версии"
								placeholder="Что поменяли. Будет видно в истории версий"
								rows={2}
								maxLength={500}
								value={comment}
								onChange={(event) => setComment(event.target.value)}
							/>
						) : null}
					</div>
				)}

				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						Отмена
					</Button>
					<Button
						disabled={!data?.canPublish || publishing || isSaving}
						onClick={() => void publish()}
					>
						{publishing ? <Loader2 className="animate-spin" /> : <Rocket />}
						Опубликовать
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
